/* eslint no-underscore-dangle: ["error", { "allow": ["_id", "__v"] }] */
import { useEffect, useState } from 'react';
import useAdminApi from '../../service/adminService';
import { Field, defaultInstructions } from './fields';
import EmailConfirmation, { emailsMatch } from './EmailConfirmation';

const empty = {
    name: '',
    billingContact: '',
    billingEmail: '',
    billingAddress: '',
    paymentInstructions: defaultInstructions,
};
export default function Organizations() {
    const api = useAdminApi();
    const [query, setQuery] = useState('');
    const [items, setItems] = useState([]);
    const [form, setForm] = useState(null);
    const [emailConfirmation, setEmailConfirmation] = useState('');
    const [savedEmail, setSavedEmail] = useState('');
    const [busy, setBusy] = useState(false);
    const [searchError, setSearchError] = useState('');
    const [formError, setFormError] = useState('');
    const [revision, setRevision] = useState(0);
    const needsEmailConfirmation = form
        && (!form._id || !emailsMatch(form.billingEmail, savedEmail));
    const emailConfirmed = !needsEmailConfirmation
        || emailsMatch(form.billingEmail, emailConfirmation);
    useEffect(() => {
        let active = true;
        const timer = setTimeout(() => {
            api(`/organizations?q=${encodeURIComponent(query)}`)
                .then((result) => {
                    if (active) {
                        setItems(result);
                        setSearchError('');
                    }
                })
                .catch((e) => {
                    if (active) setSearchError(e.message);
                });
        }, 250);
        return () => {
            active = false;
            clearTimeout(timer);
        };
    }, [api, query, revision]);
    const save = async (event) => {
        event.preventDefault();
        if (!emailConfirmed) {
            setFormError('Email addresses must match.');
            return;
        }
        setBusy(true);
        setFormError('');
        try {
            await api(`/organizations${form._id ? `/${form._id}` : ''}`, {
                method: form._id ? 'PATCH' : 'POST',
                body: form,
            });
            setFormError('');
            setForm(null);
            setRevision((value) => value + 1);
        } catch (e) {
            setFormError(e.message);
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className="po-panel">
            <h2>Purchasing organizations</h2>
            <p>
                Search before adding an organization to avoid duplicates. Updating defaults
                does not change existing orders.
            </p>
            {searchError && (
                <p
                    className="po-error"
                    role="alert">
                    {searchError}
                </p>
            )}
            <div className="po-actions po-organization-toolbar">
                <label
                    htmlFor="organizations-field-1"
                    className="po-field">
                    <span>Find organization</span>
                    <input
                        id="organizations-field-1"
                        type="search"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)} />
                </label>
                <button
                    disabled={busy}
                    type="button"
                    onClick={() => {
                        setFormError('');
                        setEmailConfirmation('');
                        setSavedEmail('');
                        setForm({ ...empty });
                    }}>
                    Add organization
                </button>
            </div>
            <ul className="po-organizations">
                {items.map((item) => (
                    <li key={item._id}>
                        <div>
                            <strong>{item.name}</strong>
                            <span>{item.billingEmail}</span>
                        </div>
                        <div className="po-actions">
                            <button
                                disabled={busy}
                                type="button"
                                onClick={() => {
                                    setFormError('');
                                    setEmailConfirmation('');
                                    setSavedEmail(item.billingEmail);
                                    setForm({ ...item });
                                }}>
                                Edit defaults
                            </button>
                        </div>
                    </li>
                ))}
            </ul>
            {!items.length && <p>No matching organizations.</p>}
            {items.length === 100 && <p>Showing the first 100 matches. Refine your search.</p>}
            {form && (
                <form
                    onSubmit={save}
                    className="po-form po-organization-form">
                    {formError && (
                        <p className="po-error" role="alert">{formError}</p>
                    )}
                    <fieldset disabled={busy}>
                        <div className="po-section-heading">
                            <div>
                                <h3>{form._id ? 'Edit organization' : 'New organization'}</h3>
                                <p>
                                    These defaults are copied into new purchase orders and can
                                    still be edited for an individual order.
                                </p>
                            </div>
                        </div>
                        <div className="po-form-sections">
                            <section className="po-form-section po-form-section-compact">
                                <div className="po-section-heading">
                                    <div>
                                        <h4>Organization</h4>
                                        <p>Use the name shown on its purchase orders.</p>
                                    </div>
                                </div>
                                <Field
                                    label="Organization name"
                                    name="name"
                                    values={form}
                                    setValues={setForm}
                                    maxLength={160}
                                    required />
                            </section>

                            <section className="po-form-section">
                                <div className="po-section-heading">
                                    <div>
                                        <h4>Billing details</h4>
                                        <p>This contact receives invoices and payment receipts.</p>
                                    </div>
                                </div>
                                <div className="po-grid">
                                    <Field
                                        label="Billing contact"
                                        name="billingContact"
                                        values={form}
                                        setValues={setForm}
                                        maxLength={200} />
                                    <Field
                                        label="Billing email"
                                        name="billingEmail"
                                        values={form}
                                        setValues={setForm}
                                        type="email"
                                        maxLength={254}
                                        required />
                                    {needsEmailConfirmation && (
                                        <div className="po-organization-email-confirmation">
                                            <EmailConfirmation
                                                label="Confirm billing email"
                                                email={form.billingEmail}
                                                value={emailConfirmation}
                                                onChange={setEmailConfirmation} />
                                        </div>
                                    )}
                                </div>
                                <Field
                                    label="Billing address"
                                    name="billingAddress"
                                    values={form}
                                    setValues={setForm}
                                    maxLength={600}
                                    multiline />
                            </section>

                            <section className="po-form-section po-form-section-compact">
                                <div className="po-section-heading">
                                    <div>
                                        <h4>Payment instructions</h4>
                                        <p>
                                            These instructions appear on this organization’s
                                            invoices.
                                        </p>
                                    </div>
                                </div>
                                <Field
                                    label="Default payment instructions"
                                    name="paymentInstructions"
                                    values={form}
                                    setValues={setForm}
                                    maxLength={1500}
                                    multiline />
                            </section>
                        </div>
                        <div className="po-form-footer">
                            <div className="po-actions">
                                <button
                                    disabled={busy || !emailConfirmed}
                                    type="submit">
                                    Save organization
                                </button>
                                <button
                                    type="button"
                                    disabled={busy}
                                    onClick={() => {
                                        setFormError('');
                                        setForm(null);
                                    }}>
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </fieldset>
                </form>
            )}
        </div>
    );
}
